import type { FC } from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactCountryIcons } from "@similarweb/icons";
import { NavItemBadge, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { EducationContainer } from "app/components/educationbar/educationContainer";
import educationHook from "app/components/educationbar/educationHook";
import { EducationIconStyle, IconButtonStyled, TitleStyle } from "app/components/React/PageTitle/styled";
import { BADGE_ITEMS } from "app/components/sidebar3/types";
import { SubNav } from "app/components/sub-navigation-bar/SubNav";
import { translate } from "app/filters/i18nFilter";
import type {
    IAiTrackerCampaign,
    ReportStatus,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import CountryService from "app/services/CountryService";

import { CampaignMenu } from "../menu/CampaignMenu";

import { BrandsBadge } from "./components/brands-indicator/BrandsBadge";
import { useLLMTabs } from "./hooks/useLLMTabs";
import { LLMCampaignsQueryBar } from "./LLMCampaignsQueryBar";
import { LLmTrackerPageFilters } from "./LLmTrackerPageFilters";
import { navigateToReport } from "./LLMTrackerUtils";

interface ILLMTrackerSubNavProps {
    selectedCampaign?: IAiTrackerCampaign;
    allCampaigns?: IAiTrackerCampaign[];
    reportStatus: ReportStatus;
    shouldDefaultOpen?: boolean;
    onCampaignSelect?: (campaign: IAiTrackerCampaign) => void;
    titleKey?: string;
}

export const LLMTrackerSubNav: FC<ILLMTrackerSubNavProps> = ({
    selectedCampaign,
    allCampaigns,
    reportStatus,
    shouldDefaultOpen = false,
    onCampaignSelect,
    titleKey = "llm.homepage.title",
}) => {
    const selectedCountryName = useMemo(
        () => CountryService.getCountryById(selectedCampaign?.countryCode)?.text,
        [selectedCampaign?.countryCode],
    );

    const {
        params: { model },
    } = useCurrentStateAndParams();

    const {
        currentTab: { id: tabId },
    } = useLLMTabs();

    const [, showEducationBar, setValues] = educationHook();

    const onEducationIconClick = () => {
        setValues(false, true);
    };

    const onCloseEducationBar = () => {
        setValues(true, false);
    };

    const handleSearchItemClick = (campaign: IAiTrackerCampaign) => {
        if (onCampaignSelect) {
            onCampaignSelect(campaign);
        } else {
            navigateToReport(campaign, model, tabId);
        }
    };

    return (
        <SubNav
            topLeftComponent={
                <LLMCampaignsQueryBar
                    campaigns={allCampaigns}
                    shouldDefaultOpen={shouldDefaultOpen}
                    onSearchItemClick={handleSearchItemClick}
                    badgeComponent={<BrandsBadge campaign={selectedCampaign} />}
                />
            }
            topRightComponent={<CampaignMenu />}
            bottomLeftComponent={
                <Flex
                    alignItems="center"
                    gap="8px"
                >
                    <TitleStyle>{translate(titleKey)}</TitleStyle>
                    {selectedCampaign?.isDemo ? (
                        <NavItemBadge
                            type={BADGE_ITEMS.INTERNAL}
                            text={translate("rankTracker.campaignsPicker.item.badge.demo")}
                        />
                    ) : null}
                    <Tooltip
                        content={
                            <Text
                                fontVariant="tooltip"
                                maxWidth="250px"
                            >
                                {selectedCountryName}
                            </Text>
                        }
                    >
                        <SWReactCountryIcons
                            countryCode={selectedCampaign?.countryCode}
                            size="xs"
                        />
                    </Tooltip>
                    <Tooltip content={translate("page.title.education.icon.tooltip")}>
                        <EducationIconStyle>
                            <IconButtonStyled
                                onClick={onEducationIconClick}
                                type="flat"
                                iconName="learning-hat"
                                iconSize="sm"
                            />
                        </EducationIconStyle>
                    </Tooltip>
                </Flex>
            }
            bottomRightComponent={
                selectedCampaign ? (
                    <LLmTrackerPageFilters
                        selectedCampaign={selectedCampaign}
                        reportStatus={reportStatus}
                    />
                ) : null
            }
            education={showEducationBar ? <EducationContainer onCloseEducationComponent={onCloseEducationBar} /> : null}
        />
    );
};
